import { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { formatDate } from "@/lib/format";
import { Search, Gift, Loader2, ShieldCheck, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const REASONS = ["customer support", "beta tester", "technical issue", "promotional courtesy"];

export default function AdminGrants() {
  const [email, setEmail] = useState("");
  const [searching, setSearching] = useState(false);
  const [target, setTarget] = useState(null);
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [reason, setReason] = useState(REASONS[0]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [granting, setGranting] = useState(false);
  const [idemKey, setIdemKey] = useState("");
  const [env, setEnv] = useState(null);

  useEffect(() => {
    api.get("/admin/environment")
      .then(({ data }) => setEnv(data.environment))
      .catch(() => setEnv(null));
  }, []);

  const search = async (e) => {
    e?.preventDefault();
    if (!email.trim()) return;
    setSearching(true);
    setTarget(null);
    setResults([]);
    setSearched(false);
    try {
      const { data } = await api.get(`/admin/users/search`, { params: { email: email.trim() } });
      const list = data.results || [];
      setSearched(true);
      if (list.length === 1) {
        setTarget(list[0]);
      } else if (list.length > 1) {
        setResults(list);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Search failed.");
    } finally {
      setSearching(false);
    }
  };

  const refreshTarget = async (userEmail) => {
    const { data } = await api.get(`/admin/users/search`, { params: { email: userEmail } });
    const list = data.results || [];
    const exact = list.find((u) => u.email === userEmail) || list[0];
    if (exact) setTarget(exact);
  };

  const openConfirm = () => {
    // One stable idempotency key per confirmation -> double-clicks can't double-grant.
    setIdemKey(crypto.randomUUID());
    setConfirmOpen(true);
  };

  const confirmGrant = async () => {
    if (granting) return;
    setGranting(true);
    try {
      const { data } = await api.post("/admin/grant-credit", {
        user_id: target.user_id,
        reason,
        idempotency_key: idemKey,
      });
      setConfirmOpen(false);
      if (data.duplicate) {
        toast.info("Already applied — no duplicate credit was added.");
      } else {
        toast.success("1 complimentary report credit granted.");
      }
      // Refresh the target view to show the updated balance + audit trail immediately.
      await refreshTarget(target.email);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Grant failed.");
    } finally {
      setGranting(false);
    }
  };

  return (
    <DashboardLayout
      title="Admin — Complimentary Credits"
      subtitle="Grant a single complimentary report credit to a specific user."
    >
      <div className="space-y-6 max-w-3xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2 rounded-lg border border-cyan-400/30 bg-cyan-400/[0.06] px-4 py-3 text-sm text-cyan-200">
            <ShieldCheck className="h-4 w-4" /> Admin-only. Every grant is audited (admin, user, time, reason).
          </div>
          {env && (
            <span
              data-testid="admin-env-badge"
              title="Deployment environment"
              className={`inline-flex items-center gap-1.5 self-start rounded-full border px-3 py-1 text-xs font-semibold tracking-wide ${
                env === "PRODUCTION"
                  ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300"
                  : "border-amber-400/40 bg-amber-400/10 text-amber-300"
              }`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${env === "PRODUCTION" ? "bg-emerald-400" : "bg-amber-400"}`} />
              {env}
            </span>
          )}
        </div>

        <form onSubmit={search} className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <Label htmlFor="admin-email" className="sr-only">User email</Label>
            <Input
              id="admin-email"
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Search user by email (exact or partial)…"
              data-testid="admin-search-email"
              className="h-11 bg-slate-900/50 border-slate-700 text-white focus-visible:ring-cyan-400"
            />
          </div>
          <Button type="submit" disabled={searching} data-testid="admin-search-btn"
            className="h-11 bg-slate-800 text-white border border-slate-700 hover:bg-slate-700">
            {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="h-4 w-4 mr-2" />Search</>}
          </Button>
        </form>

        {results.length > 1 && (
          <div className="rounded-xl border border-slate-800 bg-[#0A1124] p-4" data-testid="admin-results-list">
            <p className="text-xs uppercase tracking-wide text-slate-500 mb-3">
              {results.length} matches — select a user
            </p>
            <ul className="space-y-1.5">
              {results.map((u) => (
                <li key={u.user_id}>
                  <button
                    type="button"
                    onClick={() => { setTarget(u); setResults([]); }}
                    data-testid={`admin-result-${u.email}`}
                    className="w-full flex items-center justify-between gap-3 rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-2 text-left hover:border-cyan-400/40 hover:bg-slate-900/70 transition-colors"
                  >
                    <span className="min-w-0">
                      <span className="block text-sm text-white truncate">{u.email}</span>
                      <span className="block text-xs text-slate-500 truncate">{u.name || "—"}</span>
                    </span>
                    <span className="shrink-0 text-xs text-cyan-300">Credits: {u.credits}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {searched && !target && results.length === 0 && (
          <div className="rounded-xl border border-slate-800 bg-[#0A1124] px-4 py-6 text-center" data-testid="admin-no-user">
            <p className="text-sm text-slate-300">No user found matching “{email.trim()}”.</p>
            <p className="mt-1 text-xs text-slate-500">Try an exact email or a partial match (search is case-insensitive).</p>
          </div>
        )}

        {target && (
          <div className="rounded-xl border border-slate-800 bg-[#0A1124] p-6" data-testid="admin-user-card">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="min-w-0">
                <p className="font-display text-lg font-semibold text-white truncate">{target.name || "—"}</p>
                <p className="text-sm text-slate-400 truncate">{target.email}</p>
                <p className="mt-1 text-xs font-mono text-slate-600">{target.user_id}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Badge variant="outline" className="border-cyan-400/40 text-cyan-300" data-testid="admin-user-credits">
                    Credits: {target.credits}
                  </Badge>
                  <Badge variant="outline" className="border-slate-700 text-slate-300">
                    {target.subscription_active ? "Subscriber" : "No subscription"}
                  </Badge>
                  <Badge variant="outline" className="border-slate-700 text-slate-300">
                    Signup credit: {target.free_used ? "used" : target.free_available ? "available" : "expired"}
                  </Badge>
                </div>
              </div>
              <div className="w-full sm:w-56 shrink-0 space-y-2">
                <Label className="text-slate-300 text-xs">Reason (optional)</Label>
                <Select value={reason} onValueChange={setReason}>
                  <SelectTrigger data-testid="admin-reason-select" className="bg-slate-900/50 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A1124] border-slate-700 text-white">
                    {REASONS.map((r) => (
                      <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={openConfirm} data-testid="admin-grant-btn"
                  className="w-full bg-cyan-400 text-black font-semibold hover:bg-cyan-300">
                  <Gift className="h-4 w-4 mr-2" />Grant 1 Complimentary Report
                </Button>
              </div>
            </div>

            {target.grants?.length > 0 && (
              <div className="mt-6 border-t border-slate-800 pt-4">
                <p className="text-xs uppercase tracking-wide text-slate-500 mb-3">Grant history (audit)</p>
                <ul className="space-y-2" data-testid="admin-grant-history">
                  {target.grants.map((g) => (
                    <li key={g.grant_id} className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-2 text-sm">
                      <div className="min-w-0">
                        <span className="text-white capitalize">{g.reason}</span>
                        <span className="text-slate-500"> · by {g.admin_email}</span>
                        <div className="text-xs font-mono text-slate-600">{formatDate(g.created_at)}</div>
                      </div>
                      {g.used ? (
                        <span className="flex items-center gap-1 text-xs text-emerald-400"><CheckCircle2 className="h-3.5 w-3.5" />used</span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs text-slate-400"><XCircle className="h-3.5 w-3.5" />not used</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      <Dialog open={confirmOpen} onOpenChange={(o) => !granting && setConfirmOpen(o)}>
        <DialogContent className="bg-[#0A1124] border-slate-700 text-white" data-testid="admin-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Grant complimentary report?</DialogTitle>
            <DialogDescription className="text-slate-400">
              This adds <span className="text-cyan-300 font-semibold">exactly 1</span> report credit to{" "}
              <span className="text-white">{target?.email}</span> (reason: <span className="capitalize">{reason}</span>).
              It does not affect their signup credit, subscription, payments, or referrals.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={granting}
              className="border-slate-700 text-white hover:bg-slate-800 hover:text-white" data-testid="admin-confirm-cancel">
              Cancel
            </Button>
            <Button onClick={confirmGrant} disabled={granting} data-testid="admin-confirm-grant"
              className="bg-cyan-400 text-black font-semibold hover:bg-cyan-300">
              {granting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm & Grant"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
